import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useBulkBroadcastActionsPermission } from 'src/components/common/BulkActions/BulkBroadcastActions/useBulkBroadcastActionsPermission';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosDropdownItem } from 'src/core/POS/PosDropdown';
import { Stack } from 'src/core/ui';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { GroupListingsV2 } from 'src/modals/GroupListingsV2';
import { ContentId } from 'src/utils/constants/contentId';
import { CommandOptionType, Event } from 'src/WebApiController';

import { BulkActionItem } from '../types';

export const useBulkGroupListings = (
  event?: Event,
  multiSelectEvents?: Event[],
  disabled?: boolean
) => {
  const { hasBroadcastPermission } = useBulkBroadcastActionsPermission();
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const isMobile = useMatchMedia('mobile');

  const { setModal } = useContext(ModalContext);
  const {
    eventsExpansion: { setListItemExpansion },
  } = useCatalogDataContext();

  const groupListings = useCallback(() => {
    if (event == null) {
      return;
    }
    setModal({
      children: (
        <GroupListingsV2 event={event} multiSelectEvents={multiSelectEvents} />
      ),
      clickOutsideToClose: true,
      size: isMobile ? 'fullscreen' : 'slide-in',
    });
  }, [setModal, event, multiSelectEvents, isMobile]);

  const onLaunchDialog = useCallback(
    (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (!event?.virtId) {
        return;
      }
      setListItemExpansion(true, [event.virtId]);

      groupListings();
    },
    [event?.virtId, groupListings, setListItemExpansion]
  );

  const openDialogStableRef = useEventCallback(() => {
    if (!event?.virtId) {
      return;
    }
    setListItemExpansion(true, [event.virtId]);
    groupListings();
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingsGroupListings,
        onSelect: openDialogStableRef,
      },
      disabled: !hasBroadcastPermission,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingsGroupListings,
        disabled: !hasBroadcastPermission,
      });
    };
  }, [
    hasBroadcastPermission,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    return hasBroadcastPermission
      ? [{ title: ContentId.GroupListings, onClick: onLaunchDialog }]
      : [];
  }, [hasBroadcastPermission, onLaunchDialog]);

  return {
    dropdown: hasBroadcastPermission ? (
      <PosDropdownItem
        key="GroupListings"
        onClick={onLaunchDialog}
        disabled={disabled}
      >
        <Stack alignItems="center" gap="m" width="full">
          <Content id={ContentId.GroupListings} />
        </Stack>
      </PosDropdownItem>
    ) : null,
    actionItems,
  };
};
